import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Like {
  id: string;
  user_id: string;
  review_id: string;
  created_at: string;
}

export function useLikes(reviewId: string) {
  const [likes, setLikes] = useState<Like[]>([]);
  const [isLiked, setIsLiked] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchLikes();
  }, [reviewId]);

  const fetchLikes = async () => {
    try {
      const { data, error } = await supabase
        .from('review_likes')
        .select('*')
        .eq('review_id', reviewId);

      if (error) throw error;

      setLikes(data || []);
      
      // Check if current user has liked this review
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setIsLiked(data?.some(like => like.user_id === user.id) || false);
      }
    } catch (error) {
      console.error('Error fetching likes:', error);
    }
  };

  const toggleLike = async () => {
    setLoading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Please sign in",
          description: "You need to be signed in to like reviews.",
          variant: "destructive",
        });
        return;
      }

      if (isLiked) {
        // Unlike
        const { error } = await supabase
          .from('review_likes')
          .delete()
          .eq('review_id', reviewId)
          .eq('user_id', user.id);

        if (error) throw error;
        
        setIsLiked(false);
        setLikes(prev => prev.filter(like => like.user_id !== user.id));
      } else {
        // Like
        const { error } = await supabase
          .from('review_likes')
          .insert({
            review_id: reviewId,
            user_id: user.id
          });

        if (error) throw error;
        
        setIsLiked(true);
        fetchLikes(); // Refresh to get the new like
      }
    } catch (error: any) {
      console.error('Error toggling like:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update like status.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    likes,
    likeCount: likes.length,
    isLiked,
    toggleLike,
    loading
  };
}